export type AuditLifecycle =
  | "常態頁"
  | "現行活動"
  | "未開始活動"
  | "過期活動"
  | "活動日期待確認"
  | "歷史活動已撤下"
  | "歷史活動轉回首頁"
  | "活動已轉址"
  | "已轉址"
  | "檢查失敗";
export type AuditTriage = {
  lifecycle: AuditLifecycle;
  lifecycleEvidence: string[];
  endDate: string | null;
  priority: "優先處理" | "後續補強" | "目前穩定" | "活動另列" | "待查證";
  rank: number | null;
  score: number;
  reasons: string[];
  fixes: string[];
  matchedKeywords: string[];
  checkedAt: string;
  httpStatus: number | null;
  canonical: string | null;
};
export function auditUrlKey(value: string) {
  try {
    const u = new URL(value),
      path = decodeURIComponent(u.pathname).replace(/\/+$/, "") || "/";
    return (
      u.hostname.toLowerCase().replace(/^www\./, "") +
      path.replace(/(\/product\/detail\/\d+).*/, "$1")
    );
  } catch {
    return value;
  }
}
const norm = (s: string) =>
  s
    .toLowerCase()
    .replace(/[\s.,，。！？!?、：:；;「」『』（）()【】\[\]—_\-]/g, "")
    .replace(/卧/g, "臥");
export function auditKeywordMatches(title: string, keywords: string[]) {
  const target = norm(title)
    .split(/適用|適合|搭配|加購/)[0]
    .replace(
      /(?:倒流香|小盤香|沉香|檀香|線香|臥香|盤香|香環|環香|塔香)(?:專用)?(?:香?[爐插座盒架夾盤]|專用)/g,
      ""
    );
  return keywords.filter(k => {
    const full = norm(k),
      topic = full.replace(/品牌推薦|哪裡買|推薦|價格|天然/g, "");
    return (
      target.includes(full) || (topic.length >= 2 && target.includes(topic))
    );
  });
}
function validDate(year: string, month: string, day: string) {
  const date = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  const parsed = new Date(date);
  return !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === date
    ? date
    : null;
}
export function campaignLifecycle(input: {
  url: string;
  finalUrl: string;
  title: string;
  h1: string;
  text: string;
  date: string;
  structuredEnds?: string[];
  httpStatus?: number;
}): { lifecycle: AuditLifecycle; evidence: string[]; endDate: string | null } {
  const path = new URL(input.url).pathname;
  const campaign =
    /^\/page\/(?:13105|12658)(?:\/|$)/.test(path) ||
    /\/(onsale|coupon)\//i.test(path) ||
    /活動|雙11|雙十一|618|周年慶|週年慶|限時|福箱/.test(
      input.title + input.h1
    ) ||
    (/\/page\//.test(path) && /優惠/.test(input.title + input.h1));
  if (campaign && [404, 410].includes(input.httpStatus || 0))
    return {
      lifecycle: "歷史活動已撤下",
      evidence: [`目前回應 HTTP ${input.httpStatus}`],
      endDate: null,
    };
  if (!campaign)
    return {
      lifecycle:
        auditUrlKey(input.url) !== auditUrlKey(input.finalUrl)
          ? "已轉址"
          : "常態頁",
      evidence: [],
      endDate: null,
    };
  if (
    auditUrlKey(input.url) !== auditUrlKey(input.finalUrl) &&
    new URL(input.finalUrl).pathname === "/"
  )
    return {
      lifecycle: "歷史活動轉回首頁",
      evidence: [`目前轉到首頁 ${input.finalUrl}`],
      endDate: null,
    };
  if (auditUrlKey(input.url) !== auditUrlKey(input.finalUrl))
    return {
      lifecycle: "活動已轉址",
      evidence: [`目前轉址到 ${input.finalUrl}`],
      endDate: null,
    };
  const today = new Date(input.date).toLocaleDateString("en-CA", {
    timeZone: "Asia/Taipei",
  });
  const ends: { date: string; evidence: string; start?: string }[] = [];
  for (const raw of input.structuredEnds || []) {
    const m = /^(20\d{2})-(\d{2})-(\d{2})(?:T|$)/.exec(raw);
    const date = m && validDate(m[1], m[2], m[3]);
    if (date) ends.push({ date, evidence: `結構化活動結束日期：${raw}` });
  }
  // Full year and an explicit ending cue are required; footer and publication years are excluded.
  const ending =
    /(?:活動結束(?:日期|時間)?|活動截止(?:日期|時間)?|優惠截止(?:日期|時間)?|優惠結束(?:日期|時間)?|活動期限|優惠期限|促銷截止|促銷結束|有效期限)\s*(?:[:：為於]\s*)?(20\d{2})[年\/.\-](\d{1,2})[月\/.\-](\d{1,2})日?/g;
  for (const m of Array.from(input.text.matchAll(ending))) {
    const date = validDate(m[1], m[2], m[3]);
    if (date) ends.push({ date, evidence: m[0] });
  }
  const range =
    /(?:活動日期|活動期間|活動時間|優惠日期|優惠期間|優惠時間|促銷日期|促銷期間)\s*[:：]?\s*(20\d{2})[年\/.\-](\d{1,2})[月\/.\-](\d{1,2})日?\s*(?:~|～|至|到|－|—)\s*(?:(20\d{2})[年\/.\-])?(\d{1,2})[月\/.\-](\d{1,2})日?/g;
  for (const m of Array.from(input.text.matchAll(range))) {
    const start = validDate(m[1], m[2], m[3]),
      date = validDate(m[4] || m[1], m[5], m[6]);
    if (date && start && start <= date)
      ends.push({ date, start, evidence: m[0] });
  }
  const namedYears = Array.from(
    new Set((input.title + " " + input.h1).match(/20\d{2}(?!\d)/g) || [])
  );
  if (namedYears.length === 1) {
    const shortRange =
      /(?:活動日期|活動期間|活動時間|優惠日期|優惠期間|優惠時間|促銷日期|促銷期間)\s*[:：]?\s*(\d{1,2})[月/](\d{1,2})日?\s*(?:~|～|至|到|－|—)\s*(\d{1,2})[月/](\d{1,2})日?/g;
    for (const m of Array.from(input.text.matchAll(shortRange))) {
      const start = validDate(namedYears[0], m[1], m[2]),
        date = validDate(namedYears[0], m[3], m[4]);
      if (start && date && start <= date)
        ends.push({
          date,
          start,
          evidence: `標題年分${namedYears[0]}；活動日期${m[0].trim()}`,
        });
    }
  }
  const latest = ends.sort(
    (a, b) =>
      b.date.localeCompare(a.date) ||
      Number(Boolean(b.start)) - Number(Boolean(a.start))
  )[0];
  const future =
    /(?:活動開始|開跑日期|開始日期)[^。\n]{0,12}(20\d{2})[年\/.\-](\d{1,2})[月\/.\-](\d{1,2})/.exec(
      input.text
    );
  const explicitStart = future && validDate(future[1], future[2], future[3]);
  if (latest)
    return {
      lifecycle:
        latest.date < today
          ? "過期活動"
          : (latest.start || explicitStart || "") > today
            ? "未開始活動"
            : "現行活動",
      evidence: [latest.evidence],
      endDate: latest.date,
    };
  if (explicitStart && explicitStart > today)
    return { lifecycle: "未開始活動", evidence: [future![0]], endDate: null };
  return {
    lifecycle: "活動日期待確認",
    evidence: ["目前頁面未找到可核對的完整活動結束日期"],
    endDate: null,
  };
}
export function finalizeAuditTriage<
  T extends { url: string; normalizedUrl: string; triage: AuditTriage },
>(rows: T[]) {
  for (const row of rows) {
    row.triage.rank = null;
    if (row.triage.priority === "優先處理") row.triage.priority = "後續補強";
  }
  const candidates = rows
    .filter(
      x =>
        x.triage.lifecycle === "常態頁" &&
        x.triage.fixes.length > 0 &&
        x.triage.score > 0
    )
    .sort(
      (a, b) => b.triage.score - a.triage.score || a.url.localeCompare(b.url)
    );
  const seen = new Set<string>();
  let rank = 0;
  for (const row of candidates) {
    const key = auditUrlKey(row.triage.canonical || row.normalizedUrl);
    if (seen.has(key)) continue;
    seen.add(key);
    if (++rank <= 20) {
      row.triage.rank = rank;
      row.triage.priority = "優先處理";
    }
  }
  return rows.sort(
    (a, b) =>
      (a.triage.rank || 9999) - (b.triage.rank || 9999) ||
      b.triage.score - a.triage.score ||
      a.url.localeCompare(b.url)
  );
}
